import { requireSupabase } from "../lib/supabase";

const client = () => requireSupabase();
const toProductScope = (scope) => (scope === "organization" ? "club" : scope);
const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const asNonEmptyString = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;
const isResponseLike = (value) =>
  isRecord(value) && typeof value.clone === "function" && typeof value.text === "function";

const readFunctionErrorPayload = async (error, response) => {
  const errorResponse = [error?.context, response].find(isResponseLike);
  if (!errorResponse) return null;

  const contentType = errorResponse.headers?.get?.("content-type");
  if (contentType && !contentType.toLowerCase().includes("json")) return null;

  const responseText = await errorResponse.clone().text();
  if (!responseText.trim()) return null;

  try {
    const payload = JSON.parse(responseText);
    return isRecord(payload) ? payload : null;
  } catch (parseError) {
    if (parseError instanceof SyntaxError) return null;
    throw parseError;
  }
};

const normalizeFunctionError = async (error, response) => {
  const payload = await readFunctionErrorPayload(error, response);
  if (!payload) return error;

  const nestedError = isRecord(payload.error) ? payload.error : null;
  const code = asNonEmptyString(payload.code)
    || asNonEmptyString(nestedError?.code)
    || asNonEmptyString(payload.error);
  const detail = asNonEmptyString(payload.detail)
    || asNonEmptyString(payload.details)
    || asNonEmptyString(payload.message)
    || asNonEmptyString(nestedError?.detail)
    || asNonEmptyString(nestedError?.details)
    || asNonEmptyString(nestedError?.message);

  if (!code && !detail) return error;

  const businessError = new Error(detail || code);
  businessError.name = "TeamManagementError";
  businessError.cause = error;
  if (code) businessError.code = code;
  if (detail) businessError.detail = detail;
  return businessError;
};

export async function getMyTeamManagementSubscriptions() {
  const { data, error } = await client().rpc("get_my_team_management_subscriptions");
  if (error) throw error;
  return (data || []).map((subscription) => ({
    ...subscription,
    scope: toProductScope(subscription.scope),
  }));
}

export async function assignFederatedTeam({
  subscriptionId,
  externalTeamId,
  seasonId,
  federationId,
  name,
  competitionId,
  groupId,
  recipientEmail,
}) {
  if (recipientEmail?.trim()) {
    const { data, error, response } = await client().functions.invoke("assign-federated-team", {
      body: { subscriptionId, externalTeamId, seasonId, federationId, name, competitionId, groupId, recipientEmail },
    });
    if (error) throw await normalizeFunctionError(error, response);
    return data;
  }
  const { data, error } = await client().rpc("assign_federated_team", {
    target_subscription_id: subscriptionId,
    target_external_team_id: String(externalTeamId),
    target_season_id: String(seasonId),
    target_federation_id: String(federationId),
    target_name: name,
    target_competition_id: competitionId ? String(competitionId) : null,
    target_group_id: groupId ? String(groupId) : null,
    recipient_email: recipientEmail?.trim() || null,
  });
  if (error) throw error;
  return data;
}

export async function acceptSubscriptionInvitation(token) {
  const { data, error } = await client().rpc("accept_subscription_invitation", {
    invitation_token_hash: token,
  });
  if (error) throw error;
  return data;
}

export async function getMyPlanningTeams() {
  const { data, error } = await client().rpc("get_my_planning_teams");
  if (error) throw error;
  return data || [];
}

export async function getMyActiveTeamAssignments() {
  const { data, error } = await client()
    .from("user_team_assignments")
    .select("team_id, subscription_id")
    .is("revoked_at", null);
  if (error) throw error;
  return data || [];
}

export async function getMyReportableCompetitions() {
  const { data, error } = await client().rpc("get_my_reportable_competitions");
  if (error) throw error;
  return data || [];
}
